import { z } from "zod";
import { messageCreditsSchema } from "./message-balance-contracts.js";
/**
 * Global platform defaults. Not organization-scoped.
 * NULL on contacts and team means Unlimited. It does not mean the row is missing.
 * defaultStorageQuotaBytes is a finite decimal string. Null is rejected.
 * New organizations copy it onto OrganizationEntitlement.storageQuotaBytes
 * and, because that legacy column is NOT NULL, onto Organization.storageQuotaBytes.
 * The entitlement is the live quota. The organization column is not.
 * Support contact fields live on SystemSettings, not here.
 * BigInt storage quotas cross JSON as decimal strings.
 */
export const SYSTEM_CONFIG_SINGLETON_ID = 1;
/** 5 GiB. Initial finite default for new organizations. */
export const INITIAL_DEFAULT_STORAGE_QUOTA_BYTES = "5368709120";
export const INITIAL_SYSTEM_CONFIG = {
    defaultContactsLimit: 1000,
    defaultTeamMembersLimit: 5,
    defaultStorageQuotaBytes: INITIAL_DEFAULT_STORAGE_QUOTA_BYTES,
    defaultWhatsAppEnabled: false,
    defaultFacebookEnabled: false,
    defaultInstagramEnabled: false,
    defaultMessageBalanceUnlimited: true,
    defaultMessageCredits: null,
};
/** Signed BIGINT upper bound. Values above this cannot be stored. */
const MAX_SIGNED_BIGINT = 9223372036854775807n;
/**
 * Non-negative decimal integer that fits in PostgreSQL BIGINT.
 * Rejects leading zeros (except "0"), signs, fractions, and JSON numbers.
 */
export const storageQuotaBytesSchema = z
    .string()
    .regex(/^(0|[1-9][0-9]*)$/)
    .refine((value) => {
    try {
        return BigInt(value) <= MAX_SIGNED_BIGINT;
    }
    catch {
        return false;
    }
}, { message: "Must fit in a signed 64-bit integer" });
export const systemConfigResponseSchema = z.object({
    defaultContactsLimit: z.number().int().nullable(),
    defaultTeamMembersLimit: z.number().int().nullable(),
    defaultStorageQuotaBytes: storageQuotaBytesSchema,
    defaultWhatsAppEnabled: z.boolean(),
    defaultFacebookEnabled: z.boolean(),
    defaultInstagramEnabled: z.boolean(),
    defaultMessageBalanceUnlimited: z.boolean().default(true),
    defaultMessageCredits: messageCreditsSchema.nullable().default(null),
});
/**
 * Partial update. Omitted keys are unchanged.
 * Explicit null on contacts or team means Unlimited.
 * defaultStorageQuotaBytes must be a finite decimal string. Null is rejected in Phase 3A.
 * Unknown keys are rejected. Platform flags are booleans, not nullable.
 */
export const updateSystemConfigRequestSchema = z
    .object({
    defaultContactsLimit: z.number().int().min(0).nullable().optional(),
    defaultTeamMembersLimit: z.number().int().min(1).nullable().optional(),
    defaultStorageQuotaBytes: storageQuotaBytesSchema.optional(),
    defaultWhatsAppEnabled: z.boolean().optional(),
    defaultFacebookEnabled: z.boolean().optional(),
    defaultInstagramEnabled: z.boolean().optional(),
    defaultMessageBalanceUnlimited: z.boolean().optional(),
    defaultMessageCredits: messageCreditsSchema.nullable().optional(),
})
    .strict();
//# sourceMappingURL=system-config-contracts.js.map